import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import {
  SearchV2RequestInterface,
  SearchV2ResponseInterface,
} from '@component-library';
import { catchError, map, of, tap } from 'rxjs';
import {
  ProductInterface,
  ProviderProductInterface,
} from '../../interfaces/product/product';
import { ProductClass } from '../../classes/product/product';
import { ProductBasicUpdate } from '../../interfaces/product/product-basic-update';
import { IdentityEnum } from '../../enums/Providers/identity-enum';
import { GlobalMessageService } from '../global-services/global-message-service/global-message.service';
import { StockPriceUpdateInterface } from '../../interfaces/product/product-stock-price-update';
import { StoreService } from '../../store/store.service';
import { ProductDtoClass } from '../../classes/product/productDto';
import { ProviderProductDtoClass } from '../../classes/provider-product/provider-productDto';
import { MonitorsService } from '../monitors/monitors.service';
import { BroadcastChannelService } from '../../broadcast-channel/services/broadcast-channel.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  globalMessageService = inject(GlobalMessageService);
  broadcastChannelService = inject(BroadcastChannelService);
  monitorsService = inject(MonitorsService);
  storeService = inject(StoreService);

  constructor(private http: HttpClient) {}

  sendProductListRequest = (request: any) => {
    return this.http
      .get<{
        Exception: string;
        Items: ProductInterface[];
        Message: string;
        Page: number;
        PageSize: number;
        Status: boolean;
        TotalCount: number;
      }>(
        `${environment.baseUrl}/products/list?GetAll=${request.GetAll}&Page=${request.Page}&PageSize=${request.PageSize}`
      )
      .pipe(
        catchError(() =>
          of({
            Exception: '',
            Items: [],
            Message: '',
            Page: request.PageSize ?? 0,
            PageSize: request.PageSize ?? 0,
            Status: false,
            TotalCount: 0,
          })
        ),
        map((res: any) => {
          return res;
        })
      );
  };

  searchv2(searchv2: SearchV2RequestInterface) {
    return this.http
      .post<SearchV2ResponseInterface>(
        `${environment.baseUrl}/products/searchV2`,
        searchv2
      )
      .pipe(
        catchError(() =>
          of({
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: '',
            Status: false,
            TotalCount: 0,
          })
        ),
        map((response: SearchV2ResponseInterface) => {
          if (response && response.Status) {
            return {
              CurrentPage: response.CurrentPage || 0,
              CurrentPageSize: response.CurrentPageSize || 0,
              Items:
                response.Items?.map(
                  (f) => new ProductClass(this.storeService.store$(), f)
                ) || [],
              Message: response.Message || '',
              Status: response.Status || false,
              TotalCount: response.TotalCount || 0,
            };
          }
          return {
            CurrentPage: 0,
            CurrentPageSize: 0,
            Items: [],
            Message: '',
            Status: false,
            TotalCount: 0,
          };
        })
      );
  }

  getProducts = (id: string) => {
    return this.http
      .get<{ Product: ProductInterface }>(
        `${environment.baseUrl}/products/get?Id=${id}`
      )
      .pipe(
        catchError(() => of({ Product: {} as ProductInterface })),
        map((f) => new ProductClass(this.storeService.store$(), f?.Product))
      );
  };
  basicUpdate = (update: ProductBasicUpdate) => {
    return this.http
      .post<{
        Message: string;
        Status: boolean;
        Ids: string[];
      }>(`${environment.baseUrl}/products/basicUpdate`, update)
      .pipe(
        catchError(() =>
          of({ Message: '', Status: false, Ids: update?.ProductIds ?? [] })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Toplu Ürün Güncelle',
            infoDetail: 'Toplu ürün güncelleme işlemi başarılı',
            errorDetail: 'Toplu ürün güncelleme işlemi başarısız',
          });
        })
      );
  };

  bulkProductSyncBySku = (skuList: (string | undefined)[]) => {
    const condition = skuList.length > 1;

    return this.http
      .post(`${environment.baseUrl}/products/bulkSync`, {
        SkuList: skuList,
      })
      .pipe(
        catchError(() => of({})),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: !!f,
            summary: `${
              condition ? 'Ürünleri' : 'Ürünü'
            } Pazaryerinden Senkronize Et`,
            infoDetail: `${
              condition ? 'Ürünlerin' : 'Ürünün'
            }  Pazaryerinden Senkronizasyon İşlemi Başarılı Oldu`,
            errorDetail: `${
              condition ? 'Ürünleri' : 'Ürünü'
            }  Pazaryerinden Senkronizasyon İşlemi Başarısız Oldu`,
          });

          this.broadcastChannelService.tasksTableChannelSend(true);
          this.monitorsService.tasksRefresh.set(+new Date());
        })
      );
  };

  removeProduct = (id: string | undefined) => {
    return this.http
      .post<{ Status: boolean; Message: string; Product: ProductInterface }>(
        `${environment.baseUrl}/products/delete`,
        {
          Id: id,
        }
      )
      .pipe(
        catchError(() => of(null)),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: !!f,
            summary: 'Ürünü Sil',
            infoDetail: `Ürün Başarıyla Silindi.`,
            errorDetail: `Ürün Silme İşlemi Başarısız Oldu.`,
          });
        })
      );
  };

  updateProductContent = (product: ProductInterface) => {
    const productDto = new ProductDtoClass(product);
    productDto.ProviderProducts = [];
    return this.http
      .post<{ Status: boolean; Message: string; Product: ProductInterface }>(
        `${environment.baseUrl}/products/updateContent`,
        {
          Product: productDto,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message:
              'Ürün güncelleme sırasında bir hata oluştu. Güncelleme işlemi başarısız oldu.',
            Product: productDto,
          })
        ),
        map((res) => {
          return res;
        })
      );
  };

  stockPriceUpdate(stockPriceUpdateModel: StockPriceUpdateInterface) {
    return this.http
      .post<{
        BulkResponse: { Message: string; Status: boolean }[];
        Message: string;
        Status: boolean;
      }>(
        `${environment.baseUrl}/products/changedBulkUpdateFromAccounting`,
        stockPriceUpdateModel
      )
      .pipe(
        catchError(() =>
          of({
            BulkResponse: [],
            Message: 'Hızlı ürün güncelleme işlemi sırasında bir hata oluştu.',
            Status: false,
          })
        ),
        tap((f) => {
          this.monitorsService.tasksRefresh.set(+new Date());
          this.broadcastChannelService.tasksTableChannelSend(true);
        })
      );
  }

  addProduct = (product: ProductInterface) => {
    const productDto = new ProductDtoClass(product);
    return this.http
      .post<{ Message: string; Status: boolean; Product: ProductInterface }>(
        `${environment.baseUrl}/products/add`,
        {
          Product: productDto,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message:
              'Ürün kaydetme sırasında bir hata oluştu. Kaydetme işlemi başarısız oldu.',
            Product: productDto,
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Ürünü Ekle.',
            infoDetail: `${product?.Name} Adlı Ürün Başarıyla Eklendi`,
            errorDetail: `${product?.Name} Adlı Ürünün Ekleme İşlemi Başarısız Oldu.`,
          });
        })
      );
  };

  changeSku(OldCode: string, Code: string, IsVariantCode: boolean) {
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/products/changeSku`,
        {
          OldCode,
          Code,
          IsVariantCode,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message:
              'Stok kodu güncelleme sırasında bir hata oluştu. Güncelleme işlemi başarısız oldu.',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Stok Kodu Güncelle',
            infoDetail: `Stok kodu güncelleme işlemi başarılı`,
            errorDetail: `${f.Message}`,
          });
        })
      );
  }

  bulkRemoveProducts(ids: string[]) {
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/products/bulkDelete`,
        { Ids: ids }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message:
              'Toplu ürün silme sırasında bir hata oluştu. İşlem başarısız oldu.',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary: 'Toplu Ürün Sil',
            infoDetail: f.Message,
            errorDetail: 'Toplu ürün silme işlemi başarısız.' + f.Message,
          });
        })
      );
  }

  updateCurrencyProductPrice = (idList: string[], currency: number) => {
    const condition = idList.length > 1;
    const summary = condition
      ? 'Toplu Para Birimi Değiştir'
      : 'Kur Tipi Güncelleme';
    const infoDetail = condition
      ? 'Toplu para birimi değiştirme işlemi başarıyla sonuçlandı.'
      : 'Kur Tipi güncelleme işlemi başarılı';
    const errorDetail = condition
      ? `Toplu para birimi güncelleme işlemi başarısız. .`
      : 'Kur Tipi güncelleme işlemi başarısız. ';

    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/products/updatePriceWithCurrency`,
        {
          ProductIdList: idList,
          Currency: currency,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message: '',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f && f.Status,
            summary,
            infoDetail,
            errorDetail: errorDetail + f.Message,
          });
        })
      );
  };

  searchProductBySkuAndBarcode(key: string) {
    return this.http
      .get<{ Count: number; Message: string; Products: ProductInterface[] }>(
        `${environment.baseUrl}/products/searchByBarcodeAndSku?SearchKey=${key}`
      )
      .pipe(
        catchError(() =>
          of({
            Count: 0,
            Message: 'Ürün arama esnansında bir hata oluştu.',
            Products: [],
          })
        ),
        map((res) => {
          return {
            Count: res.Count,
            Message: res.Message,
            Products: res?.Products?.map(
              (f) => new ProductClass(this.storeService.store$(), f)
            ),
          };
        })
      );
  }

  updateBulkProduct(
    Products: ProductInterface[],
    Enumerable: any[],
    MarketplaceIdentity: IdentityEnum,
    ProviderId: string
  ) {
    const productDtos = Products.map((product) => new ProductDtoClass(product));

    return this.http.post<{
      Count: number;
      Message: string;
      Products: ProductInterface[];
      Status: boolean;
    }>(`${environment.baseUrl}/products/updateBulk`, {
      Products: productDtos,
      Enumerable,
      MarketplaceIdentity,
      ProviderId,
    });
  }

  addBulkProviderProductsBySku(providerProducts: any[], obj: any[]) {
    const providerProductDtos = providerProducts.map((providerProduct) => {
      providerProduct.Id = '';
      return new ProviderProductDtoClass(providerProduct);
    });

    return this.http.post<{
      Message: string;
      ProviderProducts: ProviderProductInterface[];
      Status: boolean;
    }>(`${environment.baseUrl}/providerProducts/addBulkByCompanyCode`, {
      ProviderProducts: providerProductDtos,
      Enumerable: obj,
    });
  }

  addBulkProduct(
    Products: ProductInterface[],
    MarketplaceIdentity: IdentityEnum,
    Enumerable: any[],
    ProviderId: string
  ) {
    Products.map((Product) => Product.ProviderProducts.map((f) => (f.Id = '')));

    const productDtos = Products.map((product) => {
      const tempProduct = new ProductDtoClass(product);
      tempProduct.Id = '';
      return tempProduct;
    });

    return this.http
      .post<{
        ElasticResponse: {
          IsValid: boolean;
          Errors: boolean;
          Message: string;
        };
        Items: any[] | null;
        Status: boolean;
        Message: string;
        TotalCount: number;
        Page: number;
        PageSize: number;
        Exception: string | null;
      }>(`${environment.baseUrl}/products/addBulk`, {
        Products: productDtos,
        Enumerable: Enumerable,
        MarketplaceIdentity: MarketplaceIdentity,
        ProviderId,
      })
      .pipe(
        tap((f) => {
          this.monitorsService.tasksRefresh.set(+new Date());
          this.broadcastChannelService.tasksTableChannelSend(true);
        })
      );
  }

  deleteProviderProduct = (ids: string[]) => {
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/providerProducts/delete`,
        {
          ids,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message: 'Pazaryeri Ürünü Silme İşlemi Başarısız Oldu',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f?.Status,
            summary: 'Pazaryeri Ürünü Silme',
            infoDetail: 'Pazaryeri Ürünü Silme İşlemi Başarılı',
            errorDetail: 'Pazaryeri Ürünü Silme İşlemi Başarısız',
          });
        })
      );
  };

  checkOnWaiting = (
    Products: any[],
    MarketplaceIdentity: number,
    AllProducts: boolean,
    ProviderId: string
  ) => {
    return this.http
      .post<{ Status: boolean; Message: string }>(
        `${environment.baseUrl}/products/manualCheckWaiting`,
        {
          Products,
          MarketplaceIdentity,
          AllProducts,
          ProviderId,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message: '',
          })
        )
      );
  };
  getListBySkuListRegex = (skuList: string[]) => {
    return this.http
      .post<{
        Status: boolean;
        Message: string;
        Items: ProductInterface[];
      }>(`${environment.baseUrl}/products/getListBySkuListRegex`, { skuList })
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message: 'Stok kodları eşleştirilirken bir hata oluştu.',
            Items: [],
          })
        )
      );
  };

  bulkUpdateDetails = (
    providerProductIds: string[],
    detailKey: string,
    detailValue: string
  ) => {
    return this.http
      .post<{ Message: string; Status: boolean }>(
        `${environment.baseUrl}/providerProducts/bulkUpdateDetails`,
        {
          ProviderProductIds: providerProductIds,
          DetailKey: detailKey,
          DetailValue: detailValue,
        }
      )
      .pipe(
        catchError(() =>
          of({
            Status: false,
            Message: 'Detay güncelleme işlemi sırasında bir hata oluştu.',
          })
        ),
        tap((f) => {
          this.globalMessageService.showStatusMessages({
            status: f?.Status,
            summary: 'Detay Güncelleme',
            infoDetail: 'Detay Güncelleme İşlemi Başarılı',
            errorDetail: 'Detay Güncelleme İşlemi Başarısız',
          });
        })
      );
  };
}
